import React, { useState } from 'react';
import { Alert, Button, Col, Row, Table, Typography } from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { validateStatus } from '../../utils/utils';
import usePost from '../../hooks/update/usePost';
import useToastContext from '../../hooks/useToastContext';
import usePutRequestUpgrade from '../../hooks/utils/usePutRequestUpgrade';
import { SectionTitleActions, SectionTitleContainer } from '../styles';
import useDeleteRequestUpgrade from '../../hooks/utils/useDeleteRequestUpgrade';
import ModalFees, { FeesFormInputs } from './modal-fees';
import config from 'config';

const Fees = ({
  fees,
  setFees,
  cardAffinityGroupId,
  setNextStep,
  action,
}: {
  fees: FeesFormInputs[];
  setFees?: (values: FeesFormInputs[]) => void;
  cardAffinityGroupId?: string | undefined;
  setNextStep?: (value: boolean) => void;
  action?: string | undefined;
}) => {
  const [feesOpen, setFeesOpen] = useState(false);
  const [editFee, setEditFee] = useState(null);
  const {error, executePost } = usePost(`v1/card_affinity_groups/${cardAffinityGroupId}/fee`);
  const { putError, executePut } = usePutRequestUpgrade({ path: `v1/card_affinity_groups/${cardAffinityGroupId}/fee/${editFee?.id}` });
  const { deleteError, executeDelete } = useDeleteRequestUpgrade({ path: `` });
  const toast = useToastContext();

  const onDeleteFee = async (value: FeesFormInputs) => {
    if(cardAffinityGroupId !== undefined && cardAffinityGroupId !== null){

      try {
        const response = await executeDelete({ url: config.api.url + `/v1/card_affinity_groups/${cardAffinityGroupId}/fee/${value?.id}` });
        if (validateStatus(response?.status)) {
          toast.success('Exito', 'Impuesto eliminado correctamente!.');
          setFees(fees.filter((item) => !(item.fee === value?.fee && item.origin === value?.origin )));
        } else {
          throw new Error();
        }

      } catch (e: unknown) {
        toast.error('Error', `Ocurrió un error: ${deleteError}, porfavor, intenta nuevamente.`);
      }
    } else{
      setFees(fees.filter((item) => !(item.fee === value?.fee && item.origin === value?.origin )));
    }
  }

  const onCreateFees = (values: FeesFormInputs) => {
    // @ts-ignore
    setFees((prev: any[]) => {
      return [...prev, values];
    });
    setFeesOpen(false);
  };

  const onUpdateFees = (value: FeesFormInputs, newValue: FeesFormInputs) => {
    const feesFiltered = fees.filter((item) => !(item.fee === value?.fee && item.origin === value?.origin));
    feesFiltered.push(newValue);
    setFees(feesFiltered);
  }

  const validateAction = async (value: FeesFormInputs) => {
    if (cardAffinityGroupId !== undefined && cardAffinityGroupId !== null) {

      if(editFee !== null) {
        try {
          const response = await executePut({ data: value });
          if (validateStatus(response?.status)) {
            setNextStep && setNextStep(true);
            toast.success('Exito', 'Impuesto actualizado correctamente!.');
            onUpdateFees(editFee, value);
          } else {
            throw new Error();
          }

        } catch (e: unknown) {
          toast.error('Error', `Ocurrió un error: ${putError}, porfavor, intenta nuevamente.`);
        }
      } else {
        try {
          const response = await executePost({ data: value });
          console.log("mostrando status", response?.status);
          if (validateStatus(response?.status)) {
            setNextStep && setNextStep(true);
            toast.success('Exito', 'Impuesto creado correctamente!.');
            onCreateFees(value);
          } else {
            throw new Error();
          }

        } catch (e: unknown) {
          toast.error('Error', `Ocurrió un error: ${error}, porfavor, intenta nuevamente.`);
        }
      }

      setEditFee(null);
    } else {
      onCreateFees(value);
    }
    setFeesOpen(false);
  };

  const editFeeExecute = (record: any) => {
    if (record?.id !== null) {
      setEditFee(record);
      setFeesOpen(true);
    }
  };

  return (
    <>
      {fees?.length > 0 ? (
        <>
          <SectionTitleContainer>
            <Typography.Title level={3}>Fees</Typography.Title>
            {
              action !== 'view' ? (
                <SectionTitleActions>
                  <Button size="middle" type="link" onClick={() => setFeesOpen(true)}>
                    Agregar
                  </Button>
                </SectionTitleActions>
              ) : ''
            }
          </SectionTitleContainer>
          <Row>
            <Col span={24}>
              <Table
                scroll={{ x: true }}
                dataSource={fees?.map((fee) => ({
                  ...fee
                }))}
                className={"icons-align-right"}
                pagination={ false }
                columns={[
                  { dataIndex: 'id', key: 'id', title: 'ID' },
                  { dataIndex: 'origin', key: 'origin', title: 'Origen' },
                  {
                    dataIndex: 'allowed',
                    key: 'allowed',
                    title: 'Habilitado',
                    render: (_, value) => {
                      return value?.allowed ? 'Si' : 'No';
                    }
                  },
                  {
                    dataIndex: 'fixed_fee',
                    key: 'fixed_fee',
                    title: 'Impuesto fijo',
                    render: (_, value ) => {
                      return new Intl.NumberFormat('es-AR', {
                        style: 'currency',
                        currency: 'ARS',
                      }).format(value?.fixed_fee)
                    }
                  },
                  { dataIndex: 'fee', key: 'fee', title: 'Impuesto' },
                  { dataIndex: 'tax_fee', key: 'tax_fee', title: 'Impuesto libre' },
                  {
                    dataIndex: 'actions',
                    key: 'actions',
                    title: 'Acciones',
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    render: (_, record) => {
                      return (
                        <Row style={{ justifyContent: 'right' }}>
                          {
                            action !== 'view' ?
                              cardAffinityGroupId !== undefined && cardAffinityGroupId !== null ? (
                                <>
                                  <Button
                                    icon={<EditOutlined />}
                                    size="small"
                                    type="link"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      editFeeExecute(record);
                                    }}
                                  />
                                  <Button
                                    icon={ <DeleteOutlined /> }
                                    size="small"
                                    type="link"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      onDeleteFee(record);
                                    }}
                                  />
                                </>
                              ): (
                                <Button
                                  icon={ <DeleteOutlined /> }
                                  size="small"
                                  type="link"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    onDeleteFee(record);
                                  }}
                                />
                              )
                              : ''
                          }
                        </Row>
                      );
                    },
                  },
                ]}
              />
            </Col>
          </Row>
        </>
      ) : (
        <Row gutter={24}>
          <Col span={12}>
            <Typography.Title level={3}>Fees</Typography.Title>
          </Col>
          <Col span={12} style={{ textAlign: 'right' }}>
            <Button size="small" type={"link"} onClick={() => setFeesOpen(true)}>
              Agregar
            </Button>
          </Col>
          <Col span={24}>
            <Alert
              message="No tienes fees para mostrar"
              type="info"
              showIcon
              style={{ marginBottom: '2rem', background: '#f8f0ff' }}
            />
          </Col>
        </Row>
      )}
      <ModalFees
        open={feesOpen}
        onCreate={validateAction}
        onCancel={() => setFeesOpen(false)}
        fee={editFee}
      />
    </>
  );
};

export default Fees;
